import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import {
  getIdToken,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from "firebase/auth";
import { MuiTelInput } from "mui-tel-input";

import { getApiUrl } from "@/constants";
import { clearPermissionsCache, seedPermissionsCache } from "@/auth-provider";
import { authObj, recaptchaConfigReady } from "@/firebase-config";
import { AuthBrandedShell } from "@/layout/AuthBrandedShell";
import { getFirebasePhoneAuthMessage } from "@/utils/firebase-auth-messages";
import { parsePermissionsArray } from "@/utils/permission-utils";

const DISTRIBUTOR_LOGIN_RECAPTCHA_ID = "distributor-login-phone-recaptcha";

/**
 * Phone-OTP login for FMB thali distributors.
 * Establishes principal=distributor claims then opens the portal.
 */
export default function FmbThaliDistributorLogin(): ReactElement {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const normalizePhoneNumber = (value: string): string => value.trim().replace(/[\s\-()]/g, "");

  useEffect(
    () => () => {
      recaptchaRef.current?.clear();
      recaptchaRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  const handleSendOtp = async (): Promise<void> => {
    setError(null);
    setInfo(null);
    const normalized = normalizePhoneNumber(phoneNumber);
    if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
      setError("Enter phone in E.164 format (e.g. +919876543210)");
      return;
    }
    if (otpCooldown > 0) {
      setInfo(`Please wait ${otpCooldown}s before requesting a new OTP`);
      return;
    }
    setLoading(true);
    try {
      await recaptchaConfigReady;
      recaptchaRef.current?.clear();
      recaptchaRef.current = new RecaptchaVerifier(authObj, DISTRIBUTOR_LOGIN_RECAPTCHA_ID, {
        size: "invisible",
      });
      const verifier = recaptchaRef.current;
      await verifier.render();
      const confirmationResult = await signInWithPhoneNumber(authObj, normalized, verifier);
      setConfirmation(confirmationResult);
      setOtpCooldown(30);
      setInfo("OTP sent. Enter the code below.");
    } catch (e: unknown) {
      setError(getFirebasePhoneAuthMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (): Promise<void> => {
    setError(null);
    const trimmedOtp = otpCode.trim();
    if (!confirmation) {
      setError("Please send OTP first");
      return;
    }
    if (!trimmedOtp) {
      setError("Please enter OTP");
      return;
    }
    setLoading(true);
    try {
      const credential = await confirmation.confirm(trimmedOtp);
      const idToken = await getIdToken(credential.user, true);
      localStorage.setItem("AUTH_TOKEN", idToken);
      localStorage.setItem("EXPIRE_TIME", String(Date.now() + 3000 * 1000));

      const establishRes = await fetch(`${getApiUrl()}/fmbDistributorPortal/establish-session`, {
        method: "POST",
        headers: {
          Authorization: idToken,
          "Content-Type": "application/json",
        },
      });
      if (!establishRes.ok) {
        const body = (await establishRes.json().catch(() => null)) as {
          message?: string | string[];
        } | null;
        const msg = Array.isArray(body?.message)
          ? body.message.join(", ")
          : body?.message || "Could not open distributor portal for this phone";
        throw new Error(msg);
      }
      const established = (await establishRes.json()) as {
        permissions?: string[];
        distributorId?: string;
      };

      const refreshed = await getIdToken(credential.user, true);
      localStorage.setItem("AUTH_TOKEN", refreshed);

      clearPermissionsCache(credential.user.uid);
      seedPermissionsCache(
        credential.user.uid,
        parsePermissionsArray(established.permissions ?? [])
      );
      if (established.distributorId) {
        localStorage.setItem("distributorPortalId", established.distributorId);
      }

      navigate("/fmb-distributor-portal");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : getFirebasePhoneAuthMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBrandedShell showLogo={false}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 380,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Distributor sign in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sign in with the phone number registered for thali distribution.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {info ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            {info}
          </Alert>
        ) : null}

        <Stack spacing={1.5}>
          <MuiTelInput
            fullWidth
            label="Phone"
            value={phoneNumber}
            onChange={setPhoneNumber}
            defaultCountry="IN"
            forceCallingCode
            disabled={loading}
          />
          <Button
            variant="outlined"
            onClick={() => void handleSendOtp()}
            disabled={loading || otpCooldown > 0}
            fullWidth
            sx={{ height: "40px", borderRadius: 3 }}
          >
            {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Send OTP"}
          </Button>
          {confirmation ? (
            <>
              <TextField
                fullWidth
                label="OTP code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={() => void handleVerifyOtp()}
                disabled={loading}
                fullWidth
                sx={{ height: "40px", borderRadius: 3 }}
              >
                Verify OTP
                {loading && <CircularProgress color="inherit" size={20} sx={{ ml: 1 }} />}
              </Button>
            </>
          ) : null}
        </Stack>

        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
          Staff login?{" "}
          <Link component={RouterLink} to="/login">
            Sign in here
          </Link>
        </Typography>
      </Paper>
      <Box id={DISTRIBUTOR_LOGIN_RECAPTCHA_ID} />
    </AuthBrandedShell>
  );
}
